import { Fragment, type Node as ProseMirrorNode } from "prosemirror-model";

import controllerModule from "@ctrl/controller";
import type {
  AgentId,
  AiAutomationPatch,
  AiAutomationSimpleTrigger,
  AiPrompt,
  AnyAgentId,
  AutomationId,
  Controller,
  DatabaseMetadata,
  Document,
  GraphCustomType,
  GraphCustomTypeField,
  GraphCustomTypeFieldDictionary,
  GraphDatabase,
  GraphDatabaseDictionary,
  GraphResult,
  NullUpsertDatabaseErrorResult,
  OrgId,
  ParentId,
  ReferenceId,
  StepId,
  TableCellPermission,
  TableId,
  TableIdUpsertDatabaseErrorResult,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import { parseSerializedAiPrompt } from "@/components/ai/prompt";
import { isDefaultExtensionTableId } from "@/components/databases";
import { parseSerializedDocument } from "@/components/document/parse";
import {
  documentToProseMirrorDoc,
  proseMirrorDocToDocument,
} from "@/components/editor/document-to-prosemirror";
import { stringAttr } from "@/components/editor/prosemirror/attributes";
import {
  addRow,
  makeGraphCommandRunner,
  setNotesField,
  setRelationField,
} from "@/components/graph/command";
import { isNoteType, isRelationType } from "@/components/graph/predicates";
import {
  fromGraphDocument,
  toGraphDocument,
} from "@/components/graph/properties/document-serialization";
import { setParent } from "@/components/graph/query";
import {
  extractRoutineDatabaseId,
  extractRoutineRowId,
  isGraphResult,
} from "@/components/graph/result";
import type { BaseLogger } from "@/components/inject";
import {
  collectAgents,
  collectAutomations,
  collectPackageDefaultEntities,
  eachPackagedNode,
  eachPackagedObject,
  packagedTreesOf,
} from "@/components/marketplace/traverse";
import type {
  Package,
  PackagedAgent,
  PackagedAutomation,
  PackagedDefaultKind,
  PackagedObject,
  PackagedPage,
  PackagedStep,
  PackagedTask,
} from "@/components/marketplace/types";
import { typeDiff } from "@/components/marketplace/utils";
import {
  definitiveTableId,
  PAGES_TABLE_ID,
  TASKS_TABLE_ID,
} from "@/components/tables";
import { isJsonRecord, type JsonValue } from "@/utils/json";
import { nanoidOfPrefixedId } from "@/utils/nanoid";

/** Describes the created root entity so callers can navigate to it. */
export type ImportedRoot =
  | { kind: "object"; databaseId: TableId; id: string }
  | { kind: "agent"; id: AnyAgentId }
  | { kind: "automation"; id: AutomationId }
  | { kind: "task"; id: string }
  | { kind: "page"; id: string };

export type InstallDatabaseMode = "create" | "use" | "extend";

export type ImportProgress = {
  done: number;
  total: number;
  message: string;
};

export type ImportProgressCallback = (p: ImportProgress) => void;

export type InstallPlan = {
  databases: {
    create: Array<{
      id: string;
      name: string;
      icon: string | null;
      note?: string | null;
    }>;
    extend: Array<{
      id: string;
      name: string;
      icon: string | null;
      note?: string | null;
    }>;
    use: Array<{
      id: string;
      name: string;
      icon: string | null;
      note?: string | null;
    }>;
  };
  objects: {
    total: number;
    byType: Array<{ name: string; icon: string | null; count: number }>;
  };
};

/**
 * Compute a human-friendly "install plan" for an import.
 *
 * This is used by the import wizard UI to summarize what will happen for each packaged database:
 * - create a new database
 * - use an existing one
 * - extend an existing one (add missing fields)
 *
 * The plan also aggregates how many rows/objects would be imported per destination.
 */
export function prepareInstall(
  pkg: Package,
  workspaceDbDict: GraphDatabaseDictionary | undefined,
  modeByDbId: Record<string, InstallDatabaseMode | undefined>,
  targetByDbId: Record<string, string | undefined>,
  includeRowsByDbId?: Record<string, boolean> | undefined
): InstallPlan {
  const dict = workspaceDbDict ?? {};

  type DbListItem = {
    id: string;
    name: string;
    icon: string | null;
    note?: string | null;
  };

  const schemaRecord = (
    v: JsonValue | null | undefined
  ): Record<string, JsonValue> | null => (v && isJsonRecord(v) ? v : null);
  const schemaMetaEmoji = (
    schema: JsonValue | null | undefined
  ): string | null => {
    const rec = schemaRecord(schema);
    const meta = rec && isJsonRecord(rec["meta"]) ? rec["meta"] : null;
    const emoji = meta ? meta["emoji"] : null;
    return typeof emoji === "string" ? emoji : null;
  };
  const schemaName = (
    schema: JsonValue | null | undefined,
    fallback: string
  ): string => {
    const rec = schemaRecord(schema);
    const dn = rec ? rec["data_name"] : null;
    const n = rec ? rec["name"] : null;
    return (
      (typeof dn === "string" && dn) || (typeof n === "string" && n) || fallback
    );
  };

  const packagedDisplay = (
    id: string
  ): { name: string; icon: string | null } => {
    const db = pkg.databases?.[id];
    const schema = db?.schema ?? null;
    const name = schemaName(schema, id);
    const icon = schemaMetaEmoji(schema);
    return { name, icon };
  };

  const workspaceDisplay = (
    db: GraphDatabase
  ): { id: string; name: string; icon: string | null } => {
    const name = db.data_name || db.name || db.id || "Database";
    const icon = db.meta?.emoji ?? null;
    return { id: String(db.id ?? name), name, icon };
  };

  const create: DbListItem[] = [];
  const extend: DbListItem[] = [];
  const use: DbListItem[] = [];

  const objectCounts = new Map<
    string,
    { name: string; icon: string | null; count: number }
  >();

  // Object ids from the main hierarchy (`pkg.root`) grouped by packaged database id.
  const hierarchyRowIdsByDbId = (() => {
    const out = new Map<string, Set<string>>();
    eachPackagedObject(pkg.root, (obj) => {
      const { tableId, rowId } = obj.meta;
      const set = out.get(tableId) ?? new Set<string>();
      set.add(rowId);
      out.set(tableId, set);
    });
    return out;
  })();

  const objectCountForPackagedDb = (
    id: string,
    includeRows: boolean
  ): number => {
    if (!includeRows) return 0;
    const hierarchy = hierarchyRowIdsByDbId.get(id) ?? new Set<string>();
    const extra =
      pkg.databases?.[id]?.rows?.reduce((sum, r) => {
        if (hierarchy.has(r.id)) return sum;
        return sum + 1;
      }, 0) ?? 0;
    return hierarchy.size + extra;
  };

  for (const id of Object.keys(pkg.databases ?? {})) {
    const mode = modeByDbId[id] ?? "create";
    const includeRows = includeRowsByDbId?.[id] !== false;
    if (mode === "create") {
      const p = packagedDisplay(id);
      create.push({ id, name: p.name, icon: p.icon });

      const rows = objectCountForPackagedDb(id, includeRows);
      const key = `create:${id}`;
      const existing = objectCounts.get(key);
      if (existing) existing.count += rows;
      else objectCounts.set(key, { name: p.name, icon: p.icon, count: rows });
      continue;
    }

    const target = targetByDbId[id];
    const workspaceDb = (() => {
      const db = target ? dict[target] : dict[id];
      if (!db) return undefined;
      // Default-type extension tables cannot be import destinations.
      return isDefaultExtensionTableId(String(db.id)) ? undefined : db;
    })();
    if (!workspaceDb) {
      const p = packagedDisplay(id);
      create.push({
        id,
        name: p.name,
        icon: p.icon,
        note: "Missing destination, will create",
      });

      const rows = objectCountForPackagedDb(id, includeRows);
      const key = `create:${id}`;
      const existing = objectCounts.get(key);
      if (existing) existing.count += rows;
      else objectCounts.set(key, { name: p.name, icon: p.icon, count: rows });
      continue;
    }

    const p = packagedDisplay(id);
    const w = workspaceDisplay(workspaceDb);
    const note = p.name !== w.name ? `from ${p.name}` : null;

    const diff = typeDiff({
      packagedSchema: pkg.databases[id].schema,
      workspaceSchema: workspaceDb,
    });
    if (diff.some((p) => p.willAdd))
      extend.push({ id: w.id, name: w.name, icon: w.icon, note });
    else use.push({ id: w.id, name: w.name, icon: w.icon, note });

    const rows = objectCountForPackagedDb(id, includeRows);
    const existing = objectCounts.get(w.id);
    if (existing) existing.count += rows;
    else objectCounts.set(w.id, { name: w.name, icon: w.icon, count: rows });
  }

  // Built-in tasks/pages travel outside `pkg.databases`: count them per kind
  // (embedded in the tree or collected through views).
  const entityCounts = { task: 0, page: 0 };
  for (const entity of collectPackageDefaultEntities(pkg)) {
    entityCounts[entity.kind] += 1;
  }
  if (entityCounts.task > 0) {
    objectCounts.set("builtin:task", {
      name: "Tasks",
      icon: null,
      count: entityCounts.task,
    });
  }
  if (entityCounts.page > 0) {
    objectCounts.set("builtin:page", {
      name: "Pages",
      icon: null,
      count: entityCounts.page,
    });
  }

  const byType = Array.from(objectCounts.values())
    .filter((v) => v.count > 0)
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

  const total = byType.reduce((sum, v) => sum + v.count, 0);

  const byName = (a: DbListItem, b: DbListItem) => a.name.localeCompare(b.name);
  create.sort(byName);
  extend.sort(byName);
  use.sort(byName);

  return {
    databases: { create, extend, use },
    objects: { total, byType },
  };
}

export type ParsedSchema = {
  typeName: string;
  databaseName: string;
  description: string;
  meta: DatabaseMetadata;
  type: GraphCustomType;
};

/**
 * Parse the packaged schema JSON into the `GraphCustomType` structures expected by the controller.
 *
 * The packaged schema is produced by exporting a `GraphDatabase` snapshot as JSON, but the import
 * path only cares about:
 * - `data_name` / `name`
 * - `meta` (emoji, title_field, color)
 * - table-origin fields (not virtual fields)
 *
 * We intentionally skip non-table fields so we only import real schema columns.
 */
const parseMeta = (v: JsonValue): DatabaseMetadata => {
  const rec = isJsonRecord(v) ? v : null;
  const emoji =
    rec && typeof rec["emoji"] === "string" ? rec["emoji"] : undefined;
  const title_field =
    rec && typeof rec["title_field"] === "string"
      ? rec["title_field"]
      : undefined;
  const colorRaw = rec ? rec["color"] : null;
  const color: DatabaseMetadata["color"] =
    Array.isArray(colorRaw) &&
    colorRaw.length === 3 &&
    typeof colorRaw[0] === "number" &&
    typeof colorRaw[1] === "number" &&
    typeof colorRaw[2] === "number"
      ? [colorRaw[0], colorRaw[1], colorRaw[2]]
      : undefined;
  return { emoji, title_field, color };
};

const parsePermissions = (v: JsonValue): TableCellPermission | undefined => {
  const rec = isJsonRecord(v) ? v : null;
  const schema =
    rec && typeof rec["schema"] === "string" ? rec["schema"] : undefined;
  const ok =
    schema === "read_only" || schema === "read_write" ? schema : undefined;
  return ok ? { schema: ok } : undefined;
};

const parseGraphCustomType = (v: JsonValue): GraphCustomType | null => {
  const rec = isJsonRecord(v) ? v : null;
  const t = rec && typeof rec["type"] === "string" ? rec["type"] : null;
  if (!t) return null;

  switch (t) {
    case "bool":
      return { type: "bool" };
    case "float":
      return { type: "float" };
    case "int":
      return { type: "int" };
    case "date":
      return { type: "date" };
    case "note":
      return { type: "note" };
    case "null":
      return { type: "null" };
    case "json":
      return { type: "json" };
    case "string": {
      if (!rec) return { type: "string" };
      const f = rec["format"];
      if (f === null) return { type: "string", format: null };
      if (f === "email" || f === "phone" || f === "uri") {
        return { type: "string", format: f };
      }
      return { type: "string" };
    }
    case "string_const": {
      const value =
        rec && typeof rec["value"] === "string" ? rec["value"] : null;
      if (!value) return null;
      return { type: "string_const", value };
    }
    case "list": {
      if (!rec) return null;
      const elements = parseGraphCustomType(rec["elements"]);
      if (!elements) return null;
      return { type: "list", elements };
    }
    case "tuple": {
      if (!rec) return null;
      const els = rec["elements"];
      if (!Array.isArray(els)) return null;
      const parsed: GraphCustomType[] = [];
      for (const e of els) {
        const pe = parseGraphCustomType(e);
        if (!pe) return null;
        parsed.push(pe);
      }
      return { type: "tuple", elements: parsed };
    }
    case "variant": {
      if (!rec) return null;
      const cases = rec["cases"];
      if (!Array.isArray(cases)) return null;
      const parsed: GraphCustomType[] = [];
      for (const c of cases) {
        const pc = parseGraphCustomType(c);
        if (!pc) return null;
        parsed.push(pc);
      }
      return { type: "variant", cases: parsed };
    }
    case "record": {
      if (!rec) return null;
      const fieldsRaw = rec["fields"];
      const fieldsRec = isJsonRecord(fieldsRaw) ? fieldsRaw : null;
      if (!fieldsRec) return { type: "record", fields: {} };
      const out: GraphCustomTypeFieldDictionary = {};
      for (const [id, fRaw] of Object.entries(fieldsRec)) {
        const fRec = isJsonRecord(fRaw) ? fRaw : null;
        if (!fRec) continue;
        const name = typeof fRec["name"] === "string" ? fRec["name"] : null;
        const value = parseGraphCustomType(fRec["value"]);
        if (!name || !value) continue;
        const permissions = parsePermissions(fRec["permissions"]);
        const description =
          typeof fRec["description"] === "string"
            ? fRec["description"]
            : undefined;
        const field: GraphCustomTypeField = {
          name,
          value,
          ...(description ? { description } : {}),
          ...(permissions ? { permissions } : {}),
        };
        out[id] = field;
      }
      return { type: "record", fields: out };
    }
    case "relation": {
      if (!rec) return null;
      const table = rec["table"];
      const tableOk = typeof table === "string" ? table : null;
      if (!tableOk) return null;
      const reverseRaw = rec["reverse"];
      const reverseRec = isJsonRecord(reverseRaw) ? reverseRaw : null;
      const reverse =
        reverseRec &&
        typeof reverseRec["field_id"] === "string" &&
        typeof reverseRec["field_name"] === "string"
          ? {
              field_id: reverseRec["field_id"],
              field_name: reverseRec["field_name"],
            }
          : undefined;
      return reverse
        ? { type: "relation", table: tableOk, reverse }
        : { type: "relation", table: tableOk };
    }
    default:
      return null;
  }
};

/**
 * Rewrite relation targets in a packaged type using `dbIdMap`.
 *
 * Packaged schemas can reference relation targets by packaged table id.
 * During import we may create new tables, so we need to update relation targets to point to the
 * actual destination table ids in the current workspace.
 */
export const rewriteRelationTargets = (
  t: GraphCustomType,
  dbIdMap: Record<string, TableId>
): GraphCustomType => {
  switch (t.type) {
    case "list":
      return { ...t, elements: rewriteRelationTargets(t.elements, dbIdMap) };
    case "tuple":
      return {
        ...t,
        elements: t.elements.map((e) => rewriteRelationTargets(e, dbIdMap)),
      };
    case "variant":
      return {
        ...t,
        cases: t.cases.map((c) => rewriteRelationTargets(c, dbIdMap)),
      };
    case "record": {
      const out: GraphCustomTypeFieldDictionary = {};
      for (const [id, f] of Object.entries(t.fields)) {
        out[id] = { ...f, value: rewriteRelationTargets(f.value, dbIdMap) };
      }
      return { ...t, fields: out };
    }
    case "relation": {
      const mapped = dbIdMap[t.table];
      return mapped ? { ...t, table: mapped } : t;
    }
    default:
      return t;
  }
};

/**
 * Parse a single packaged database schema JSON into a `ParsedSchema` for import.
 *
 * Returns `null` if the schema is missing required fields or can't be interpreted.
 */
export const parsePackagedSchema = (
  schema: JsonValue | null | undefined
): ParsedSchema | null => {
  if (!schema || !isJsonRecord(schema)) return null;
  const typeName =
    typeof schema["data_name"] === "string" ? schema["data_name"] : null;
  const databaseName =
    typeof schema["name"] === "string" ? schema["name"] : null;
  if (!typeName || !databaseName) return null;

  const meta = parseMeta(schema["meta"]);
  const description =
    typeof schema["description"] === "string" ? schema["description"] : "";

  const dbTypeRaw = schema["type"];
  const dbTypeRec = isJsonRecord(dbTypeRaw) ? dbTypeRaw : null;
  const fieldsRaw =
    dbTypeRec && isJsonRecord(dbTypeRec["fields"]) ? dbTypeRec["fields"] : null;
  const outFields: GraphCustomTypeFieldDictionary = {};
  if (fieldsRaw) {
    for (const [id, fRaw] of Object.entries(fieldsRaw)) {
      if (!isJsonRecord(fRaw)) continue;
      if (fRaw["origin"] !== "table") continue;
      const name = typeof fRaw["name"] === "string" ? fRaw["name"] : null;
      const value = parseGraphCustomType(fRaw["value"]);
      if (!name || !value) continue;
      const permissions = parsePermissions(fRaw["permissions"]);
      const description =
        typeof fRaw["description"] === "string"
          ? fRaw["description"]
          : undefined;
      const field: GraphCustomTypeField = {
        name,
        value,
        ...(description ? { description } : {}),
        ...(permissions ? { permissions } : {}),
      };
      outFields[id] = field;
    }
  }

  return {
    typeName,
    databaseName,
    description,
    meta,
    type: { type: "record", fields: outFields },
  };
};

type PackagePromptRegistry = Map<JsonValue, AiPrompt>;

const isWhitespaceByte = (character: string): boolean =>
  character === " " ||
  character === "\t" ||
  character === "\n" ||
  character === "\v" ||
  character === "\f" ||
  character === "\r";

const hasNonWhitespaceByte = (value: string): boolean =>
  Array.from(value).some((character) => !isWhitespaceByte(character));

/**
 * Validate every serialized prompt before import starts mutating the workspace.
 * The registry keeps the parsed dates and typed context for the later rewrite.
 */
export const preflightPackagePrompts = (
  pkg: Package,
  makeBlockId: () => string = controllerModule.makeBlockId
): PackagePromptRegistry => {
  const prompts: PackagePromptRegistry = new Map();
  const add = (value: JsonValue, location: string): AiPrompt => {
    const prompt = parseSerializedAiPrompt(value, makeBlockId);
    if (!prompt) {
      throw new Error(`Invalid AI prompt in ${location}`);
    }
    prompts.set(value, prompt);
    return prompt;
  };

  eachPackagedNode(pkg.root, (node) => {
    if (node.kind === "step") {
      const prompt = add(node.meta.prompt, `step ${node.meta.id}`);
      if (!node.meta.name || !hasNonWhitespaceByte(node.meta.name)) {
        throw new Error(`Invalid AI step ${node.meta.id}: name is required`);
      }
      if (prompt.document.blocks.length === 0) {
        throw new Error(`Invalid AI step ${node.meta.id}: prompt is empty`);
      }
    } else if (node.kind === "agent") {
      add(node.meta.agent_instructions, `agent ${node.meta.id} instructions`);
      add(node.meta.user_preferences, `agent ${node.meta.id} preferences`);
      add(node.meta.working_memory, `agent ${node.meta.id} working memory`);
    }
  });

  return prompts;
};

/** Parsed notes of each packaged built-in task/page (`null` when absent). */
type PackageEntityRegistry = Map<PackagedTask | PackagedPage, Document | null>;

/**
 * Validate every packaged built-in task/page before import starts mutating
 * the workspace: a usable title, parseable notes, and a well-formed extension
 * row. The registry keeps the parsed notes for the later rewrite.
 */
export const preflightPackageEntities = (
  pkg: Package,
  makeBlockId: () => string = controllerModule.makeBlockId
): PackageEntityRegistry => {
  const out: PackageEntityRegistry = new Map();
  for (const entity of collectPackageDefaultEntities(pkg)) {
    const location = `${entity.kind} ${entity.meta.id}`;
    if (
      typeof entity.meta.title !== "string" ||
      !hasNonWhitespaceByte(entity.meta.title)
    ) {
      throw new Error(`Invalid ${location}: title is required`);
    }
    let notes: Document | null = null;
    if (entity.meta.notes !== undefined) {
      notes = parseSerializedDocument(entity.meta.notes, makeBlockId);
      if (!notes) throw new Error(`Invalid notes in ${location}`);
    }
    const extensionRow = entity.meta.extensionRow;
    if (
      extensionRow !== undefined &&
      (typeof extensionRow.rowId !== "string" ||
        !isJsonRecord(extensionRow.properties))
    ) {
      throw new Error(`Invalid extension row in ${location}`);
    }
    out.set(entity, notes);
  }
  return out;
};

const isOkAdd = (
  r: TableIdUpsertDatabaseErrorResult
): r is { value: TableId } => "value" in r && typeof r.value === "string";

const isOkUpdate = (r: NullUpsertDatabaseErrorResult): r is { value: null } =>
  "value" in r;

// NOTE: `import` is a reserved keyword in TypeScript/ESM, so we can’t name this function `import()`.
/**
 * Apply a packaged extension into a workspace by creating/reusing/extending databases.
 *
 * High-level algorithm:
 * - Decide for each packaged DB whether to create, use, or extend (based on user selection + availability).
 * - Create new databases in two passes:
 *   1) create without relation fields whose targets aren't created yet
 *   2) update the created databases to the full schema once all table IDs are known
 * - Extend existing databases by adding missing fields (matching by normalized field name)
 *
 * Returns a mapping from packaged database ids → destination table ids (`dbIdMap`),
 * which callers can use to import rows and rewrite relation targets.
 */
export async function importPackage(
  controller: Controller,
  baseLogger: BaseLogger,
  workspaceId: WorkspaceId,
  pkg: Package,
  workspaceDbDict: GraphDatabaseDictionary | undefined,
  modeByDbId: Record<string, InstallDatabaseMode | undefined>,
  targetByDbId: Record<string, TableId | undefined>,
  includeRowsByDbId?: Record<string, boolean> | undefined,
  onProgress?: ImportProgressCallback,
  organizationId?: OrgId,
  /**
   * The importing user's personal workspace — where built-in tasks/pages'
   * extension tables live. Required when the package contains packaged
   * tasks/pages (threaded from the caller's injected profile).
   */
  personalWorkspaceId?: WorkspaceId,
  /**
   * Agent to own the root automation when the package root is an automation
   * (packages never carry the source account's agent, so the importer picks
   * one). `null`/`undefined` imports it as an orphan.
   */
  rootAutomationAgentId?: AnyAgentId | null
): Promise<{
  dbIdMap: Record<string, TableId>;
  /**
   * Packaged block id → freshly generated id, for every block written by
   * this import (task/page notes, notes-typed fields, prompts). Lets the
   * caller reattach per-block data that traveled keyed by the packaged ids
   * — notably {@link Package.viewConfigurations} (see
   * `applyViewConfigurations`).
   */
  blockIdMap: Record<string, string>;
  rootObject?: { databaseId: TableId; id: string };
  root?: ImportedRoot;
}> {
  const parsedPackagePrompts = preflightPackagePrompts(pkg);
  const parsedEntityNotes = preflightPackageEntities(pkg);
  const defaultEntities = collectPackageDefaultEntities(pkg);
  if (defaultEntities.length > 0 && !personalWorkspaceId) {
    throw new Error(
      "This extension contains tasks/pages but no personal workspace was provided"
    );
  }
  // Built-in tasks/pages are personal-organization objects: their extension
  // tables and hierarchy live in the importing user's personal workspace, so
  // such a package must never install into a shared workspace. The import UI
  // pins the destination accordingly; this guards the invariant itself.
  if (defaultEntities.length > 0 && workspaceId !== personalWorkspaceId) {
    throw new Error(
      "This extension contains tasks/pages and can only be installed into the personal workspace"
    );
  }
  const logger = baseLogger.forModule("Import");
  const dict = workspaceDbDict ?? {};
  const existingDatabaseNames = new Set<string>(
    Object.values(dict)
      .map((db) => db.name)
      .filter((n): n is string => typeof n === "string" && n.length > 0)
  );
  const dbNameByPackagedDbId: Record<string, string> = {};
  const dbNameByPackagedName: Record<string, string> = {};

  logger.debug("importPackage() start", {
    workspaceId,
    packageVersion: pkg.version,
    packagedDatabaseCount: Object.keys(pkg.databases ?? {}).length,
    includeRowsOverridesCount: Object.keys(includeRowsByDbId ?? {}).length,
    existingDatabaseNamesCount: existingDatabaseNames.size,
  });

  const dbIdMap: Record<string, TableId> = {};
  const toCreate: string[] = [];
  const toExtend: Array<{ packagedId: string; targetId: TableId }> = [];

  for (const packagedId of Object.keys(pkg.databases ?? {})) {
    const mode = modeByDbId[packagedId] ?? "create";
    if (mode === "create") {
      logger.debug("db mapping: create", { packagedId, mode });
      toCreate.push(packagedId);
      continue;
    }
    const target = targetByDbId[packagedId] ?? packagedId;
    if (!dict[String(target)]) {
      // Missing mapping: fall back to create.
      logger.debug("db mapping: missing target, fallback to create", {
        packagedId,
        mode,
        target,
      });
      toCreate.push(packagedId);
      continue;
    }
    if (isDefaultExtensionTableId(String(target))) {
      // Default-type extension tables (Task/Event/Page/Person…) are not valid
      // import destinations: their Title/Notes live on the built-in entity
      // (managed by dedicated RPCs) and rows must be linked via extension_row,
      // which the generic row import cannot do. Fall back to create.
      logger.debug(
        "db mapping: target is a default-type extension table, fallback to create",
        { packagedId, mode, target }
      );
      toCreate.push(packagedId);
      continue;
    }
    dbIdMap[packagedId] = target;
    logger.debug("db mapping: use existing", {
      packagedId,
      mode,
      target,
    });
    if (mode === "extend") toExtend.push({ packagedId, targetId: target });
  }

  const requiredPackagedDbIds = (() => {
    const out = new Set<string>();
    for (const tree of packagedTreesOf(pkg)) {
      eachPackagedObject(tree, (obj) => {
        out.add(obj.meta.tableId);
      });
    }
    return out;
  })();

  const shouldImportDb = (packagedDbId: string): boolean =>
    requiredPackagedDbIds.has(packagedDbId) ||
    includeRowsByDbId?.[packagedDbId] !== false;

  const rowsToCreateCount = (() => {
    const seen = new Set<string>(); // `${dbId}/${rowId}`
    const add = (dbId: string, rowId: string) => {
      if (!dbId || !rowId) return;
      const key = `${dbId}/${rowId}`;
      if (seen.has(key)) return;
      seen.add(key);
    };

    // Always include the hierarchy objects (root tree + view-collected
    // entities' trees).
    for (const tree of packagedTreesOf(pkg)) {
      eachPackagedObject(tree, (obj) => {
        add(obj.meta.tableId, obj.meta.rowId);
      });
    }

    // Include additional packaged rows (e.g. view/relation/reference databases) only when enabled.
    for (const packagedDbId of Object.keys(pkg.databases ?? {})) {
      if (!shouldImportDb(packagedDbId)) continue;
      for (const row of pkg.databases?.[packagedDbId]?.rows ?? []) {
        if (!row || typeof row.id !== "string") continue;
        add(packagedDbId, row.id);
      }
    }

    return seen.size;
  })();

  // Track progress as "work over the two passes" (create + finalize, rows and
  // built-in entities alike) so we can show one continuous progress bar.
  const progressTotal = (rowsToCreateCount + defaultEntities.length) * 2;
  let progressDone = 0;
  const progressSet = (message: string) => {
    onProgress?.({ done: progressDone, total: progressTotal, message });
  };

  // Initial state so UI shows total immediately.
  progressDone = 0;
  progressSet("Preparing install…");

  const preparingDone = (() => {
    progressSet("Preparing install…");
    return () => {
      // Keep progress counter stable; only update message.
      progressSet("Preparing install…");
    };
  })();

  const packagedSchemaById = new Map<string, ParsedSchema>();
  const schemaParseFailures: string[] = [];
  for (const packagedId of Object.keys(pkg.databases ?? {})) {
    progressSet(`Parsing packaged schema ${packagedId}`);
    const schema = pkg.databases[packagedId]?.schema ?? null;
    const parsed = parsePackagedSchema(schema);
    if (parsed) packagedSchemaById.set(packagedId, parsed);
    else schemaParseFailures.push(packagedId);
  }
  preparingDone();
  if (schemaParseFailures.length > 0) {
    logger.debug("parsePackagedSchema failures", {
      count: schemaParseFailures.length,
      packagedIds: schemaParseFailures,
    });
  }

  // For databases that already map to an existing destination (use/extend),
  // record a mapping from the packaged internal database name → destination internal name.
  // This is later used to rewrite query blocks in Notes that start with the database name.
  for (const packagedId of Object.keys(pkg.databases ?? {})) {
    const parsed = packagedSchemaById.get(packagedId);
    const destId = dbIdMap[packagedId];
    if (!parsed || !destId) continue;
    const destDb = dict[String(destId)];
    if (!destDb) continue;
    dbNameByPackagedDbId[packagedId] = destDb.name;
    dbNameByPackagedName[parsed.databaseName.trim()] = destDb.name;
  }

  const randomTwoDigits = (): string =>
    String(Math.floor(Math.random() * 100)).padStart(2, "0");

  const allocateUniqueDatabaseName = (base: string): string => {
    const baseName = base.trim();
    if (!existingDatabaseNames.has(baseName)) {
      existingDatabaseNames.add(baseName);
      return baseName;
    }
    for (let attempt = 0; attempt < 25; attempt += 1) {
      const candidate = `${baseName}_${randomTwoDigits()}`;
      if (!existingDatabaseNames.has(candidate)) {
        existingDatabaseNames.add(candidate);
        return candidate;
      }
    }
    // Extremely unlikely fallback: deterministic scan.
    for (let n = 0; n < 100; n += 1) {
      const candidate = `${baseName}_${String(n).padStart(2, "0")}`;
      if (!existingDatabaseNames.has(candidate)) {
        existingDatabaseNames.add(candidate);
        return candidate;
      }
    }
    // Last resort: include timestamp.
    const candidate = `${baseName}_${Date.now()}`;
    existingDatabaseNames.add(candidate);
    return candidate;
  };

  logger.debug("schema install plan", {
    toCreateCount: toCreate.length,
    toExtendCount: toExtend.length,
    toUseCount: Object.keys(dbIdMap).length - toExtend.length,
  });

  const unresolvedRelationTarget = (
    t: GraphCustomType,
    knownIds: Set<string>
  ): boolean => {
    if (t.type === "relation") {
      const table = t.table;
      return packagedSchemaById.has(table) && !knownIds.has(table);
    }
    if (t.type === "list")
      return unresolvedRelationTarget(t.elements, knownIds);
    if (t.type === "tuple")
      return t.elements.some((e) => unresolvedRelationTarget(e, knownIds));
    if (t.type === "variant")
      return t.cases.some((c) => unresolvedRelationTarget(c, knownIds));
    if (t.type === "record")
      return Object.values(t.fields).some((f) =>
        unresolvedRelationTarget(f.value, knownIds)
      );
    return false;
  };

  // 1) Create databases (without relation fields that point to not-yet-created tables).
  for (const packagedId of toCreate) {
    const parsed = packagedSchemaById.get(packagedId);
    if (!parsed)
      throw new Error(`Missing packaged schema for database ${packagedId}`);

    progressSet(`Creating database ${parsed.typeName}`);
    const originalDatabaseName = parsed.databaseName;
    const databaseNameTrimmed = originalDatabaseName.trim();
    const databaseNameCollides = existingDatabaseNames.has(databaseNameTrimmed);
    logger.debug("database.create check name", {
      packagedId,
      originalDatabaseName,
      databaseNameTrimmed,
      collides: databaseNameCollides,
    });
    const createdDatabaseName =
      allocateUniqueDatabaseName(originalDatabaseName);
    dbNameByPackagedDbId[packagedId] = createdDatabaseName;
    dbNameByPackagedName[databaseNameTrimmed] = createdDatabaseName;
    if (createdDatabaseName !== originalDatabaseName) {
      logger.debug("database.create check name collision", {
        packagedId,
        originalDatabaseName,
        createdDatabaseName,
      });
    }

    const known = new Set<string>(Object.keys(dbIdMap));
    const filteredFields: GraphCustomTypeFieldDictionary = {};
    const allFields = Object.entries(
      parsed.type.type === "record" ? parsed.type.fields : {}
    );
    for (const [fid, f] of allFields) {
      if (unresolvedRelationTarget(f.value, known)) continue;
      filteredFields[fid] = {
        ...f,
        value: rewriteRelationTargets(f.value, dbIdMap),
      };
    }
    const droppedFieldCount =
      allFields.length - Object.keys(filteredFields).length;
    logger.debug("database.create (pass1)", {
      packagedId,
      typeName: parsed.typeName,
      databaseName: parsed.databaseName,
      createdDatabaseName,
      knownTableIdsCount: known.size,
      fieldCount: allFields.length,
      droppedFieldCount,
    });
    const createType: GraphCustomType = {
      type: "record",
      fields: filteredFields,
    };

    const res = await controller.graph.database.add(
      workspaceId,
      parsed.typeName,
      createdDatabaseName,
      parsed.meta,
      createType,
      parsed.description
    );
    if (!isOkAdd(res)) {
      logger.error("database.create failed", {
        packagedId,
        typeName: parsed.typeName,
        databaseName: parsed.databaseName,
        createdDatabaseName,
        errorKind: res.error.error_kind,
      });
      throw new Error(
        `Failed to create database ${parsed.typeName}: ${res.error.error_kind}`
      );
    }
    dbIdMap[packagedId] = res.value;
    logger.debug("database.create ok", {
      packagedId,
      createdTableId: res.value,
    });
  }

  const graphDatabaseToGraphResultDatabase = (
    db: GraphDatabase
  ): Record<string, GraphResult> => {
    const meta: Record<string, GraphResult> = {};
    if (typeof db.meta.emoji === "string") meta["emoji"] = db.meta.emoji;
    if (Array.isArray(db.meta.color)) meta["color"] = db.meta.color;
    if (typeof db.meta.title_field === "string")
      meta["title_field"] = db.meta.title_field;
    return {
      id: db.id,
      name: db.name,
      data_name: db.data_name,
      workspace: db.workspace,
      meta,
    };
  };

  const dbById = new Map<string, GraphDatabase>();
  for (const db of Object.values(dict)) dbById.set(String(db.id), db);

  // Fetch databases we just created so relation payloads can embed a consistent database record.
  let fetchedCreatedCount = 0;
  for (const packagedId of toCreate) {
    const createdId = dbIdMap[packagedId];
    if (!createdId) continue;
    if (dbById.has(String(createdId))) continue;
    progressSet(`Fetching database ${String(createdId)}`);
    const fetched = (
      await controller.graph.database.get(workspaceId, createdId)
    ).release();
    if (fetched) {
      dbById.set(String(createdId), fetched);
      fetchedCreatedCount += 1;
    } else {
      logger.debug("database.get after create returned null", {
        packagedId,
        createdId,
      });
    }
  }
  if (fetchedCreatedCount > 0) {
    logger.debug("database.get after create complete", {
      fetchedCreatedCount,
    });
  }

  // 2) Update created databases to full schemas (now that all IDs are known).
  for (const packagedId of toCreate) {
    const createdId = dbIdMap[packagedId];
    const parsed = packagedSchemaById.get(packagedId);
    if (!createdId || !parsed) continue;
    progressSet(`Finalizing schema ${parsed.typeName}`);
    const fullType = rewriteRelationTargets(parsed.type, dbIdMap);
    const finalDatabaseName =
      dbNameByPackagedDbId[packagedId] ?? parsed.databaseName;
    logger.debug("database.create (pass2 finalize schema)", {
      packagedId,
      createdId,
      typeName: parsed.typeName,
      databaseName: parsed.databaseName,
      finalDatabaseName,
    });
    const res = await controller.graph.database.update(
      workspaceId,
      createdId,
      parsed.typeName,
      finalDatabaseName,
      parsed.meta,
      fullType,
      parsed.description
    );
    if (!isOkUpdate(res)) {
      logger.error("database.finalize failed", {
        packagedId,
        createdId,
        typeName: parsed.typeName,
        databaseName: parsed.databaseName,
        finalDatabaseName,
        errorKind: res.error.error_kind,
      });
      throw new Error(
        `Failed to finalize database ${parsed.typeName}: ${res.error.error_kind}`
      );
    }
    logger.debug("database.finalize ok", {
      packagedId,
      createdId,
    });
  }

  // 3) Extend existing databases by adding missing fields (by name).
  for (const { packagedId, targetId } of toExtend) {
    const parsed = packagedSchemaById.get(packagedId);
    const existing = dict[String(targetId)];
    if (!parsed || !existing) continue;

    progressSet(`Extending database ${existing.data_name}`);
    const existingFields: GraphCustomTypeFieldDictionary = {};
    for (const [fid, f] of Object.entries(existing.type?.fields ?? {})) {
      if (f.origin !== "table") continue;
      existingFields[fid] = {
        name: f.name,
        value: f.value,
        ...(f.description ? { description: f.description } : {}),
        ...(f.permissions ? { permissions: f.permissions } : {}),
      };
    }

    const existingByName = new Map<string, string>();
    for (const [fid, f] of Object.entries(existingFields)) {
      existingByName.set(f.name.trim().toLowerCase(), fid);
    }

    const nextFields: GraphCustomTypeFieldDictionary = { ...existingFields };
    let addedCount = 0;
    let generatedIdsCount = 0;
    for (const [fid, f] of Object.entries(
      parsed.type.type === "record" ? parsed.type.fields : {}
    )) {
      const key = f.name.trim().toLowerCase();
      if (existingByName.has(key)) continue;
      // Prefer keeping the packaged field id so reverse relations can stay consistent.
      const newId = nextFields[fid]
        ? await controller.graph.database.generateColumnId()
        : fid;
      if (newId !== fid) generatedIdsCount += 1;
      nextFields[newId] = {
        ...f,
        value: rewriteRelationTargets(f.value, dbIdMap),
      };
      addedCount += 1;
    }

    logger.debug("database.extend", {
      packagedId,
      targetId,
      existingFieldCount: Object.keys(existingFields).length,
      addedCount,
      generatedIdsCount,
    });

    const nextType: GraphCustomType = { type: "record", fields: nextFields };
    const res = await controller.graph.database.update(
      workspaceId,
      targetId,
      existing.data_name,
      existing.name,
      existing.meta,
      nextType,
      existing.description
    );
    if (!isOkUpdate(res)) {
      logger.error("database.extend failed", {
        packagedId,
        targetId,
        errorKind: res.error.error_kind,
      });
      throw new Error(
        `Failed to extend database ${existing.data_name}: ${res.error.error_kind}`
      );
    }
    logger.debug("database.extend ok", { packagedId, targetId });
  }

  // 3b) Built-in extension tables (packaged tasks/pages). They live in the
  // importing user's *personal* workspace, are singletons that can only be
  // extended (never created), and their extension rows are created per-entity
  // below — never through the generic row passes. When the package snapshots
  // an extension schema, add its missing custom fields (matching by name,
  // like step 3); either way, register the table so relation values and
  // extension-row fields resolve through the usual id maps.
  const extensionTableByKind = new Map<PackagedDefaultKind, GraphDatabase>();
  if (defaultEntities.length > 0 && personalWorkspaceId) {
    const kinds = new Set<PackagedDefaultKind>(
      defaultEntities.map((entity) => entity.kind)
    );
    for (const kind of kinds) {
      progressSet(`Preparing ${kind} extensions`);
      const existing = (
        await controller.graph.database.defaultTypes.get(
          personalWorkspaceId,
          kind
        )
      ).release();
      if (!existing) {
        // Without the extension table the packaged entities would install
        // stripped of their custom fields, relations, and page hierarchy —
        // fail loudly rather than degrade silently (the table is provisioned
        // with the personal workspace, so this is a broken account state).
        logger.error("extension table missing", { kind, personalWorkspaceId });
        throw new Error(
          `The personal workspace has no ${kind} extension table`
        );
      }
      extensionTableByKind.set(kind, existing);
      const canonicalId = kind === "task" ? TASKS_TABLE_ID : PAGES_TABLE_ID;
      dbIdMap[canonicalId] = existing.id;
      dbById.set(String(existing.id), existing);

      const packagedSchema = pkg.extensions?.[kind];
      const parsed =
        packagedSchema === undefined
          ? null
          : parsePackagedSchema(packagedSchema);
      if (packagedSchema !== undefined && !parsed) {
        logger.debug("extension schema parse failed", { kind });
      }
      if (!parsed) continue;
      packagedSchemaById.set(canonicalId, parsed);
      if (String(existing.id) !== canonicalId) {
        packagedSchemaById.set(String(existing.id), parsed);
      }

      const existingFields: GraphCustomTypeFieldDictionary = {};
      for (const [fid, f] of Object.entries(existing.type?.fields ?? {})) {
        if (f.origin !== "table") continue;
        existingFields[fid] = {
          name: f.name,
          value: f.value,
          ...(f.description ? { description: f.description } : {}),
          ...(f.permissions ? { permissions: f.permissions } : {}),
        };
      }
      const existingByName = new Set<string>();
      for (const f of Object.values(existingFields)) {
        existingByName.add(f.name.trim().toLowerCase());
      }
      const nextFields: GraphCustomTypeFieldDictionary = { ...existingFields };
      let addedCount = 0;
      for (const [fid, f] of Object.entries(
        parsed.type.type === "record" ? parsed.type.fields : {}
      )) {
        if (existingByName.has(f.name.trim().toLowerCase())) continue;
        const newId = nextFields[fid]
          ? await controller.graph.database.generateColumnId()
          : fid;
        nextFields[newId] = {
          ...f,
          value: rewriteRelationTargets(f.value, dbIdMap),
        };
        addedCount += 1;
      }
      if (addedCount === 0) continue;
      logger.debug("extension table extend", { kind, addedCount });
      const res = await controller.graph.database.update(
        existing.workspace,
        existing.id,
        existing.data_name,
        existing.name,
        existing.meta,
        { type: "record", fields: nextFields },
        existing.description
      );
      if (!isOkUpdate(res)) {
        logger.error("extension table extend failed", {
          kind,
          errorKind: res.error.error_kind,
        });
        throw new Error(
          `Failed to extend the ${kind} extensions: ${res.error.error_kind}`
        );
      }
    }
  }

  const runCommand = makeGraphCommandRunner(
    controller,
    workspaceId,
    baseLogger,
    "Import"
  );

  logger.debug("required packaged databases (from hierarchy)", {
    count: requiredPackagedDbIds.size,
    packagedDbIds: Array.from(requiredPackagedDbIds.values()),
  });

  type PackagedRowToCreate = {
    packagedDbId: string;
    packagedRowId: string;
    data: JsonValue;
  };

  const rowsToCreate = (() => {
    const out: PackagedRowToCreate[] = [];
    const seen = new Set<string>(); // `${dbId}/${rowId}`

    const push = (
      packagedDbId: string,
      packagedRowId: string,
      data: JsonValue
    ) => {
      const key = `${packagedDbId}/${packagedRowId}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ packagedDbId, packagedRowId, data });
    };

    // Always include the hierarchy objects (required DBs are derived from
    // these) — the root tree's and the view-collected entities'.
    for (const tree of packagedTreesOf(pkg)) {
      eachPackagedObject(tree, (obj) => {
        push(obj.meta.tableId, obj.meta.rowId, obj.meta.properties);
      });
    }

    // Include additional packaged rows (e.g. view/relation databases) only when enabled.
    for (const packagedDbId of Object.keys(pkg.databases ?? {})) {
      if (!shouldImportDb(packagedDbId)) continue;
      for (const row of pkg.databases?.[packagedDbId]?.rows ?? []) {
        push(packagedDbId, row.id, row.data);
      }
    }

    return out;
  })();
  {
    const byDb: Record<string, number> = {};
    for (const r of rowsToCreate) {
      byDb[r.packagedDbId] = (byDb[r.packagedDbId] ?? 0) + 1;
    }
    logger.debug("rows to create", {
      total: rowsToCreate.length,
      byPackagedDbId: byDb,
    });
  }

  const fieldTypeByName = (
    packagedDbId: string
  ): Map<string, GraphCustomType> => {
    const parsed = packagedSchemaById.get(packagedDbId);
    const out = new Map<string, GraphCustomType>();
    if (!parsed) return out;
    if (parsed.type.type !== "record") return out;
    for (const f of Object.values(parsed.type.fields)) {
      out.set(f.name, f.value);
    }
    return out;
  };

  const rowIdMap = new Map<string, Map<string, string>>(); // packagedDbId -> (packagedRowId -> newRowId)

  // Packaged host of each hierarchy node: an object/task/page embedded
  // through another node's notes is that node's child, recorded as the
  // child's parent pointer right after it is created (the notes written in
  // pass 2 are only the visual half of the relationship; without the pointer
  // the Pages tree and breadcrumbs would show every imported node as a
  // root). First embed wins when a node appears in several notes, nodes
  // embedded in AI prompts (agents/automations/steps) have no notes parent,
  // and the package root always stays parentless.
  type PackagedHost = PackagedObject | PackagedTask | PackagedPage;
  const packagedKeyOf = (node: PackagedHost): string =>
    node.kind === "object"
      ? `object:${node.meta.tableId}/${node.meta.rowId}`
      : `${node.kind}:${node.meta.id}`;
  const hostByPackagedKey = (() => {
    const out = new Map<string, PackagedHost>();
    const rootKey =
      pkg.root.kind === "object" ||
      pkg.root.kind === "task" ||
      pkg.root.kind === "page"
        ? packagedKeyOf(pkg.root)
        : null;
    for (const tree of packagedTreesOf(pkg)) {
      eachPackagedNode(tree, (node) => {
        if (
          node.kind !== "object" &&
          node.kind !== "task" &&
          node.kind !== "page"
        ) {
          return;
        }
        for (const child of Object.values(node.content.objects ?? {})) {
          if (
            child.kind !== "object" &&
            child.kind !== "task" &&
            child.kind !== "page"
          ) {
            continue;
          }
          const key = packagedKeyOf(child);
          if (key === rootKey || out.has(key)) continue;
          out.set(key, node);
        }
      });
    }
    return out;
  })();

  const objectLabelFromRow = (data: JsonValue, fallback: string): string => {
    const rec = isJsonRecord(data) ? data : null;
    if (!rec) return fallback;
    const pick = (k: string): string | null => {
      const v = rec[k];
      if (typeof v !== "string") return null;
      const s = v.trim();
      return s.length ? s : null;
    };
    const preferred =
      pick("Title") || pick("title") || pick("Name") || pick("name");
    if (preferred) return preferred;
    for (const [k, v] of Object.entries(rec)) {
      if (!k || k.startsWith("_")) continue;
      if (typeof v !== "string") continue;
      const s = v.trim();
      if (!s) continue;
      return s;
    }
    return fallback;
  };

  // 4) Create all rows with non-relation/non-notes properties to get fresh IDs.
  progressSet(`Creating objects…`);
  let createdRowIndex = 0;
  for (const { packagedDbId, packagedRowId, data } of rowsToCreate) {
    createdRowIndex += 1;
    const destDbIdForMessage = dbIdMap[packagedDbId];
    const destDbForMessage = destDbIdForMessage
      ? (dbById.get(String(destDbIdForMessage)) ?? null)
      : null;
    const destLabel =
      destDbForMessage?.data_name ??
      destDbForMessage?.name ??
      (destDbIdForMessage ? String(destDbIdForMessage) : packagedDbId);
    const objectLabel = objectLabelFromRow(data, packagedRowId);
    const message = `(${createdRowIndex}/${rowsToCreate.length}) Creating object ${objectLabel} as ${destLabel}`;
    progressSet(message);
    try {
      const destDbId = dbIdMap[packagedDbId];
      if (!destDbId) {
        logger.debug("row.create skipped: missing dbIdMap entry", {
          packagedDbId,
          packagedRowId,
        });
        continue;
      }

      const byName = fieldTypeByName(packagedDbId);
      const dataRec = isJsonRecord(data) ? data : null;
      if (!dataRec) {
        logger.debug("row.create skipped: non-record data", {
          packagedDbId,
          packagedRowId,
        });
        continue;
      }

      const fields: Record<string, GraphResult> = {};
      let skippedRelationOrNotes = 0;
      let skippedUnknownField = 0;
      for (const [name, raw] of Object.entries(dataRec)) {
        if (!name || name.startsWith("_")) continue;
        const t = byName.get(name);
        // Only set schema-backed fields during `rows add { ... }`.
        // In particular, note-typed fields are set in pass 2.
        if (!t) {
          skippedUnknownField += 1;
          continue;
        }
        if (isRelationType(t) || isNoteType(t)) {
          skippedRelationOrNotes += 1;
          continue;
        }
        fields[name] = raw;
      }
      logger.debug("row.create (pass1)", {
        packagedDbId,
        packagedRowId,
        destDbId,
        setFieldCount: Object.keys(fields).length,
        skippedRelationOrNotes,
        skippedUnknownField,
      });
      const createdId = await addRow(runCommand, destDbId, fields);

      const byRow = rowIdMap.get(packagedDbId) ?? new Map<string, string>();
      byRow.set(packagedRowId, createdId);
      rowIdMap.set(packagedDbId, byRow);
      logger.debug("row.create ok", {
        packagedDbId,
        packagedRowId,
        createdId,
      });

      // Record the packaged hierarchy in `_routine.parent`. The hierarchy is
      // traversed depth-first from the root, so the parent's row is always
      // created (and its fresh id known) before its children's. A single
      // `rows add` cannot carry `_routine.parent` — like the in-app creation
      // path, the pointer takes a dedicated `setParent` command. Task/page
      // hosts are deferred to the entity pass below (their entities don't
      // exist yet).
      //
      // Known gap: this only rebuilds hierarchy derived from the package
      // structure (embedding). Rows pulled in bulk through a database's
      // sample/all export carry their raw `_routine.parent` in `data`, but
      // that pointer targets the exporter's ids and is skipped with the
      // other `_`-prefixed fields — parent/child structure among such rows
      // is not recreated.
      const packagedParent = hostByPackagedKey.get(
        `object:${packagedDbId}/${packagedRowId}`
      );
      if (packagedParent && packagedParent.kind === "object") {
        const parentDbId = dbIdMap[packagedParent.meta.tableId];
        const parentRowId = rowIdMap
          .get(packagedParent.meta.tableId)
          ?.get(packagedParent.meta.rowId);
        if (!parentDbId || !parentRowId) {
          logger.debug("row.parent skipped: missing parent mapping", {
            packagedDbId,
            packagedRowId,
            parentPackagedDbId: packagedParent.meta.tableId,
            parentPackagedRowId: packagedParent.meta.rowId,
          });
        } else {
          const parentObjectId = await controller.graph.object.id.make({
            workspace_id: workspaceId,
            table_id: parentDbId,
            row_id: parentRowId,
          });
          await setParent(
            workspaceId,
            {
              tableId: destDbId,
              rowId: createdId,
              parentId: parentObjectId,
            },
            controller
          );
          logger.debug("row.parent set", {
            packagedDbId,
            packagedRowId,
            createdId,
            parentObjectId,
          });
        }
      }
    } finally {
      progressDone += 1;
      progressSet(message);
    }
  }

  const mapRowId = (
    packagedDbId: string,
    packagedRowId: string
  ): string | null =>
    rowIdMap.get(packagedDbId)?.get(packagedRowId) ??
    mapDefaultEntityReference(packagedDbId, packagedRowId);

  // 4b) Create the built-in tasks/pages, before pass 2 so notes rewriting can
  // repoint their todo blocks / page embeds / references. Only template-able
  // data is recreated (title, starred, url — notes follow in the entity
  // finalize pass); extension rows are created in the personal workspace's
  // extension tables and linked through `extension_row`. Hosts come first
  // (`collectDefaultEntities` visits a node before its children), so parent
  // pointers resolve: tasks record theirs natively at creation, pages on
  // their extension row (`_routine.parent`, the only place a notes-hosted
  // page's parent lives — see `@/page`).
  const taskIdMap = new Map<string, string>(); // packaged task id -> new id
  const pageIdMap = new Map<string, string>(); // packaged page id -> new id

  // References INTO the Task/Page extension tables (relation cells, legacy
  // object embeds) resolve through the entity id maps: on the exporter side
  // such a reference carries either the target's native id (`task:<nanoid>` —
  // what live virtual rows surface) or its materialized extension row's id
  // (`row:<nanoid>`, sharing the entity's nanoid on live data). Neither is in
  // `rowIdMap` unless the target traveled with a packaged extension row, so
  // fall back to the entity maps and answer with the imported entity's
  // *native* id — the shape the app itself writes, valid whether or not the
  // new entity has a materialized extension row.
  const mapDefaultEntityReference = (
    packagedDbId: string,
    packagedRowId: string
  ): string | null => {
    // Packaged graph values can carry alias ids for the built-in tables
    // (see `definitiveTableId`); normalize before matching.
    const canonicalDbId = definitiveTableId(packagedDbId);
    const kind =
      canonicalDbId === TASKS_TABLE_ID
        ? "task"
        : canonicalDbId === PAGES_TABLE_ID
          ? "page"
          : null;
    if (!kind) return null;
    const nanoid =
      nanoidOfPrefixedId(kind, packagedRowId) ??
      nanoidOfPrefixedId("row", packagedRowId);
    if (!nanoid) return null;
    const idMap = kind === "task" ? taskIdMap : pageIdMap;
    return idMap.get(`${kind}:${nanoid}`) ?? null;
  };

  const personalRunner = personalWorkspaceId
    ? makeGraphCommandRunner(
        controller,
        personalWorkspaceId,
        baseLogger,
        "Import"
      )
    : null;

  // A built-in entity encoded as a graph object id — the only shape
  // `_routine.parent` can hold: the kind's extension table plus the entity's
  // nanoid as the row (see `makeDefaultParentObjectId` in `@/components/databases`).
  const encodedDefaultParentId = async (
    kind: PackagedDefaultKind,
    entityId: string
  ): Promise<string | null> => {
    const nanoid = entityId.split(":")[1];
    const db = extensionTableByKind.get(kind);
    if (!nanoid || !db) return null;
    return controller.graph.object.id.make({
      workspace_id: db.workspace,
      table_id: db.id,
      row_id: `row:${nanoid}`,
    });
  };

  // The imported host as a `_routine.parent`-compatible graph object id.
  const hostParentPointerOf = async (
    host: PackagedHost | undefined
  ): Promise<string | null> => {
    if (!host) return null;
    if (host.kind === "object") {
      const destDbId = dbIdMap[host.meta.tableId];
      const destRowId = mapRowId(host.meta.tableId, host.meta.rowId);
      if (!destDbId || !destRowId) return null;
      return controller.graph.object.id.make({
        workspace_id: workspaceId,
        table_id: destDbId,
        row_id: destRowId,
      });
    }
    const mapped =
      host.kind === "task"
        ? taskIdMap.get(host.meta.id)
        : pageIdMap.get(host.meta.id);
    return mapped ? encodedDefaultParentId(host.kind, mapped) : null;
  };

  // The imported host as a typed `ParentId` (native task parents).
  const hostParentIdOf = async (
    host: PackagedHost | undefined
  ): Promise<ParentId | null> => {
    if (!host) return null;
    if (host.kind === "object") {
      const destDbId = dbIdMap[host.meta.tableId];
      const destRowId = mapRowId(host.meta.tableId, host.meta.rowId);
      if (!destDbId || !destRowId) return null;
      const objectId = await controller.graph.object.id.make({
        workspace_id: workspaceId,
        table_id: destDbId,
        row_id: destRowId,
      });
      return { kind: "object", id: objectId };
    }
    const mapped =
      host.kind === "task"
        ? taskIdMap.get(host.meta.id)
        : pageIdMap.get(host.meta.id);
    return mapped ? { kind: host.kind, id: mapped } : null;
  };

  // Create the entity's extension row with its non-relation/non-notes custom
  // fields (relations/notes follow in the entity finalize pass, like rows).
  const createExtensionRow = async (
    entity: PackagedTask | PackagedPage
  ): Promise<string | null> => {
    const db = extensionTableByKind.get(entity.kind);
    if (!db || !personalRunner) return null;
    const fields: Record<string, GraphResult> = {};
    const ext = entity.meta.extensionRow;
    if (ext && isJsonRecord(ext.properties)) {
      const byName = fieldTypeByName(String(db.id));
      for (const [name, raw] of Object.entries(ext.properties)) {
        if (!name || name.startsWith("_")) continue;
        const t = byName.get(name);
        if (!t || isRelationType(t) || isNoteType(t)) continue;
        fields[name] = raw;
      }
    }
    const createdId = await addRow(personalRunner, db.id, fields);
    if (ext) {
      const byRow = rowIdMap.get(String(db.id)) ?? new Map<string, string>();
      byRow.set(ext.rowId, createdId);
      rowIdMap.set(String(db.id), byRow);
    }
    return createdId;
  };

  // The created extension row of each entity, for the parent pass below
  // (a page's task parent is recorded on its extension row).
  const extRowIdByEntityKey = new Map<string, string>();

  let createdEntityIndex = 0;
  for (const entity of defaultEntities) {
    createdEntityIndex += 1;
    const message = `(${createdEntityIndex}/${defaultEntities.length}) Creating ${entity.kind} ${entity.meta.title}`;
    progressSet(message);
    try {
      const host = hostByPackagedKey.get(packagedKeyOf(entity));
      if (entity.kind === "task") {
        const parent = await hostParentIdOf(host);
        const newId = await controller.task.create(
          {
            title: entity.meta.title,
            ...(entity.meta.starred === true ? { starred: true } : {}),
            ...(typeof entity.meta.url === "string"
              ? { url: entity.meta.url }
              : {}),
            ...(parent ? { parent } : {}),
          },
          null
        );
        taskIdMap.set(entity.meta.id, newId);
        if (entity.meta.extensionRow) {
          const extRowId = await createExtensionRow(entity);
          if (extRowId) {
            extRowIdByEntityKey.set(packagedKeyOf(entity), extRowId);
            await controller.task.update(
              newId,
              { extension_row: extRowId },
              "app",
              null
            );
          }
        }
        logger.debug("task.create ok", { packagedId: entity.meta.id, newId });
      } else {
        const newId = await controller.page.create(
          entity.meta.title,
          null,
          "app"
        );
        pageIdMap.set(entity.meta.id, newId);
        // The extension row also carries the page's parent pointer, so it is
        // needed whenever the page has custom fields, a host, or a packaged
        // parent (see the parent pass below).
        if (
          entity.meta.extensionRow ||
          host ||
          entity.meta.parent !== undefined
        ) {
          const extRowId = await createExtensionRow(entity);
          if (extRowId) {
            extRowIdByEntityKey.set(packagedKeyOf(entity), extRowId);
            await controller.page.update(
              newId,
              { extension_row: extRowId },
              "app"
            );
            const db = extensionTableByKind.get("page");
            const parentPointer = await hostParentPointerOf(host);
            if (db && parentPointer) {
              await setParent(
                db.workspace,
                { tableId: db.id, rowId: extRowId, parentId: parentPointer },
                controller
              );
            }
          }
        }
        logger.debug("page.create ok", { packagedId: entity.meta.id, newId });
      }
    } finally {
      progressDone += 1;
      progressSet(message);
    }
  }

  // Packaged parent pointers (`meta.parent`): hierarchy for entities that are
  // not embedded in their parent's notes (typically view-collected subtasks/
  // subpages). Runs after every entity exists so forward pointers resolve;
  // the embedding host — recorded at creation — always wins, and parents
  // outside the package are dropped.
  for (const entity of defaultEntities) {
    const packagedParent = entity.meta.parent;
    if (!packagedParent) continue;
    if (hostByPackagedKey.has(packagedKeyOf(entity))) continue;
    const mappedParent =
      packagedParent.kind === "task"
        ? taskIdMap.get(packagedParent.id)
        : pageIdMap.get(packagedParent.id);
    if (!mappedParent) {
      logger.debug("entity.parent skipped: parent not part of the import", {
        kind: entity.kind,
        packagedId: entity.meta.id,
        parent: packagedParent,
      });
      continue;
    }
    if (entity.kind === "task") {
      const newId = taskIdMap.get(entity.meta.id);
      if (!newId) continue;
      await controller.task.update(
        newId,
        { parent: { kind: packagedParent.kind, id: mappedParent } },
        "app",
        null
      );
    } else {
      const newId = pageIdMap.get(entity.meta.id);
      if (!newId) continue;
      // A page's parent — page and task alike — lives on its extension row
      // (`_routine.parent`): the native `page.parent` is never written, see
      // `@/page` and `movePageToParent`.
      const extRowId = extRowIdByEntityKey.get(packagedKeyOf(entity));
      const db = extensionTableByKind.get("page");
      const parentPointer = await encodedDefaultParentId(
        packagedParent.kind,
        mappedParent
      );
      if (extRowId && db && parentPointer) {
        await setParent(
          db.workspace,
          { tableId: db.id, rowId: extRowId, parentId: parentPointer },
          controller
        );
      }
    }
    logger.debug("entity.parent set", {
      kind: entity.kind,
      packagedId: entity.meta.id,
      parent: packagedParent,
      mappedParent,
    });
  }

  // Object rows hosted by a task/page: record their parent pointer now that
  // the host entity exists (deferred from pass 1).
  for (const { packagedDbId, packagedRowId } of rowsToCreate) {
    const host = hostByPackagedKey.get(
      `object:${packagedDbId}/${packagedRowId}`
    );
    if (!host || host.kind === "object") continue;
    const destDbId = dbIdMap[packagedDbId];
    const destRowId = mapRowId(packagedDbId, packagedRowId);
    if (!destDbId || !destRowId) continue;
    const parentId = await hostParentPointerOf(host);
    if (!parentId) {
      logger.debug("row.parent skipped: missing entity host mapping", {
        packagedDbId,
        packagedRowId,
        hostKind: host.kind,
        hostPackagedId: host.meta.id,
      });
      continue;
    }
    await setParent(
      workspaceId,
      { tableId: destDbId, rowId: destRowId, parentId },
      controller
    );
    logger.debug("row.parent set (entity host)", {
      packagedDbId,
      packagedRowId,
      destRowId,
      parentId,
    });
  }

  const renameQueryDatabaseNameViaViewConfig = async (
    q: string
  ): Promise<string> => {
    // Rewrite via structured view config:
    // parse query -> edit `source_table_name` -> regenerate query.
    //
    // If parsing/building fails, or no mapping applies, return original query.
    try {
      const res = await controller.graph.view.ofQuery(workspaceId, q);
      if (!("value" in res)) return q;
      const viewWithHints = res.value;
      const from = (viewWithHints.source_table_name || "").trim();
      if (!from) return q;
      const mapped =
        dbNameByPackagedName[from] ?? dbNameByPackagedName[from.trim()];
      if (!mapped || mapped === from) return q;

      // Strip hints so we can pass a clean GraphViewConfig to `toQuery`.

      const { available_columns: _availableColumns, ...cfg } = viewWithHints;
      const queryString = await controller.graph.view.toQuery({
        ...cfg,
        source_table_name: mapped,
      });
      logger.debug("notes.query.rename(view)", {
        fromDatabaseName: from,
        toDatabaseName: mapped,
        before: q,
        after: queryString,
      });
      return queryString;
    } catch {
      return q;
    }
  };

  // Rewrite the embedded object/reference/query ids of a ProseMirror document
  // to point at the freshly-imported objects. Shared by notes rewriting and AI
  // prompt rewriting.
  const rewritePmDoc = async (
    pmDoc: ProseMirrorNode
  ): Promise<ProseMirrorNode> => {
    const stats = {
      objectEmbeds: 0,
      objectEmbedsDropped: 0,
      pageEmbeds: 0,
      pageEmbedsDropped: 0,
      todoBlocksSeen: 0,
      todoBlocksUnlinked: 0,
      recurrenceBlocksUnlinked: 0,
      queryBlocksSeen: 0,
      queryBlocksRenamed: 0,
      queryBlocksUnmapped: 0,
      referencesSeen: 0,
      referencesRewritten: 0,
      referencesUnmapped: 0,
      referencesParseFailed: 0,
    };

    const rewriteQuery = async (source: string): Promise<string> => {
      stats.queryBlocksSeen += 1;
      const rewritten = await renameQueryDatabaseNameViaViewConfig(source);
      if (rewritten === source) stats.queryBlocksUnmapped += 1;
      else stats.queryBlocksRenamed += 1;
      return rewritten;
    };

    const rewriteNode = async (
      node: ProseMirrorNode
    ): Promise<ProseMirrorNode | null> => {
      const t = node.type.name;

      if (t === "object") {
        // The modern `{referee}` block form: a referenced native page, whose
        // `page:` id sits in `objectId` while the triple attrs are null.
        const refereeKind = stringAttr(node, "refereeKind") ?? null;
        if (refereeKind !== null) {
          if (refereeKind !== "page") return node;
          stats.pageEmbeds += 1;
          const packagedPageId = stringAttr(node, "objectId") ?? null;
          const mappedPageId = packagedPageId
            ? (pageIdMap.get(packagedPageId) ?? null)
            : null;
          if (!mappedPageId) {
            // Unlike a todo, an object block has no legal detached form:
            // drop it, like an unmapped legacy object embed.
            stats.pageEmbedsDropped += 1;
            logger.debug("notes.page embed dropped: missing page mapping", {
              packagedPageId,
            });
            return null;
          }
          return node.type.create(
            { ...node.attrs, objectId: mappedPageId },
            node.content,
            node.marks
          );
        }

        stats.objectEmbeds += 1;
        const databaseId = stringAttr(node, "databaseId") ?? null;
        const objectId = stringAttr(node, "objectId") ?? null;
        if (!databaseId || !objectId) return node;

        const destTableId = dbIdMap[databaseId] ?? databaseId;
        const destRowId = mapRowId(databaseId, objectId);
        if (!destRowId) {
          stats.objectEmbedsDropped += 1;
          logger.debug("notes.object embed dropped: missing row mapping", {
            packagedTableId: databaseId,
            packagedRowId: objectId,
          });
          return null;
        }
        return node.type.create(
          {
            ...node.attrs,
            workspaceId: workspaceId,
            databaseId: destTableId,
            objectId: destRowId,
          },
          node.content,
          node.marks
        );
      }

      // Todo blocks backed by a native task (the ProseMirror node type is
      // "task"): repoint at the imported task, or unlink when the backing
      // task wasn't packaged — a detached todo is legal, and keeping the
      // exporter's id would drive a foreign (or nonexistent) task.
      if (t === "task") {
        const packagedTaskId = stringAttr(node, "taskId") ?? null;
        if (packagedTaskId === null) return node;
        stats.todoBlocksSeen += 1;
        const mappedTaskId = taskIdMap.get(packagedTaskId) ?? null;
        if (!mappedTaskId) stats.todoBlocksUnlinked += 1;
        return node.type.create(
          { ...node.attrs, taskId: mappedTaskId },
          node.content,
          node.marks
        );
      }

      // Recurrent-task blocks: recurrences are never packaged (scheduling
      // is exporter-specific), so there is nothing to repoint at — always
      // unlink, or the copy would drive the exporter's recurrence (or a
      // nonexistent one). A detached recurrent-task block is legal, like a
      // detached todo.
      if (t === "recurrent_task") {
        if (stringAttr(node, "taskId") === null) return node;
        stats.recurrenceBlocksUnlinked += 1;
        return node.type.create(
          { ...node.attrs, taskId: null },
          node.content,
          node.marks
        );
      }

      if (t === "query") {
        const source = stringAttr(node, "source") ?? "";
        const next = await rewriteQuery(source);
        if (next === source) return node;
        return node.type.create(
          { ...node.attrs, source: next },
          node.content,
          node.marks
        );
      }

      if (t === "reference") {
        stats.referencesSeen += 1;
        const id = stringAttr(node, "id") ?? null;
        const kind = stringAttr(node, "kind") ?? null;
        if (!id || !kind) return node;
        // References to built-in tasks/pages carry the native id directly.
        // Unmapped ones are left as-is, like unmapped object references.
        if (kind === "task" || kind === "page") {
          const mapped =
            kind === "task" ? taskIdMap.get(id) : pageIdMap.get(id);
          if (!mapped) {
            stats.referencesUnmapped += 1;
            return node;
          }
          stats.referencesRewritten += 1;
          return node.type.create(
            { ...node.attrs, id: mapped },
            node.content,
            node.marks
          );
        }
        if (kind !== "object") return node;

        let parsed: {
          workspace_id?: unknown;
          table_id?: unknown;
          row_id?: unknown;
        };
        try {
          parsed = await controller.graph.object.id.parse(id);
        } catch {
          stats.referencesParseFailed += 1;
          return node;
        }
        const tableId =
          typeof parsed.table_id === "string" ? parsed.table_id : null;
        const rowId = typeof parsed.row_id === "string" ? parsed.row_id : null;
        if (!tableId || !rowId) return node;

        const destTableId = dbIdMap[tableId] ?? tableId;
        const destRowId = mapRowId(tableId, rowId);
        if (!destRowId) {
          stats.referencesUnmapped += 1;
          logger.debug("notes.reference unmapped (missing row mapping)", {
            packagedTableId: tableId,
            packagedRowId: rowId,
            referenceId: id,
          });
          return node;
        }

        const newId = await controller.graph.object.id.make({
          workspace_id: workspaceId,
          table_id: destTableId,
          row_id: destRowId,
        });
        stats.referencesRewritten += 1;
        return node.type.create(
          { ...node.attrs, id: newId },
          node.content,
          node.marks
        );
      }

      if (node.isText) return node;

      // Recurse into content.
      if (node.content.childCount === 0) return node;
      let changed = false;
      const children: ProseMirrorNode[] = [];
      for (let i = 0; i < node.content.childCount; i += 1) {
        const child = node.child(i);
        const next = await rewriteNode(child);
        if (!next) {
          changed = true;
          continue;
        }
        if (next !== child) changed = true;
        children.push(next);
      }
      if (!changed) return node;
      return node.type.create(
        node.attrs,
        Fragment.fromArray(children),
        node.marks
      );
    };

    const rewritten = (await rewriteNode(pmDoc)) ?? pmDoc;
    logger.debug("notes.rewrite", stats);
    return rewritten;
  };

  // Every imported document gets fresh block ids: block ids are only unique
  // per *export*, so writing the packaged ids would make the copies share
  // per-block browser state with the originals (and with other installs of
  // the same package) — see the block-id doc in `types.ts`. The mapping is
  // returned so per-block data that traveled keyed by the packaged ids can
  // reattach ({@link Package.viewConfigurations}). Runs after `rewritePmDoc`,
  // whose lookups (embedded objects by block id) need the packaged ids.
  const blockIdMap: Record<string, string> = {};
  const regenerateBlockIds = (doc: Document): Document => ({
    blocks: doc.blocks.map((block) => {
      const id = controllerModule.makeBlockId();
      blockIdMap[block.id] = id;
      return { ...block, id };
    }),
  });

  const rewriteNotesDocument = async (
    v: JsonValue
  ): Promise<GraphResult | null> => {
    if (!v || !isJsonRecord(v)) return null;
    if (!Array.isArray(v["blocks"])) return null;

    // Rewriting Notes is easiest/most-correct at the ProseMirror layer, because:
    // - object embeds and query blocks are block nodes
    // - references are inline atom nodes
    // - rich text markup parsing/hydration happens when converting Document -> ProseMirror
    let pmDoc: ProseMirrorNode;
    try {
      const doc = fromGraphDocument(v);
      pmDoc = documentToProseMirrorDoc(doc, baseLogger);
    } catch (e) {
      logger.debug("notes.rewrite failed to parse document", {
        error: e instanceof Error ? e.message : String(e),
      });
      return null;
    }

    const rewrittenPm = await rewritePmDoc(pmDoc);

    // Fresh ids via `regenerateBlockIds`, NOT `controller.document.removeMetadata`:
    // besides regenerating ids (without recording the mapping), that RPC
    // also nulls todo/recurrent-task links — undoing the repointing at the
    // imported task copies that `rewritePmDoc` just performed.
    const rewrittenDoc = regenerateBlockIds(
      proseMirrorDocToDocument(rewrittenPm)
    );

    // Notes must be encoded in the same GraphResult shape used by GraphNotes:
    // `{ _routine: { type: "notes" }, blocks: [...] }`
    // Otherwise the graph command compiler will reject the assignment.
    return toGraphDocument({ blocks: rewrittenDoc.blocks });
  };

  // Rewrite an AI prompt document (not a notes GraphResult).
  const rewritePromptDocument = async (
    document: Document
  ): Promise<Document> => {
    const pmDoc = documentToProseMirrorDoc(document, baseLogger);
    const rewrittenPm = await rewritePmDoc(pmDoc);
    return regenerateBlockIds(proseMirrorDocToDocument(rewrittenPm));
  };

  // Rewrite a single `ReferenceId` (object/task/page ids) to its imported
  // target; other kinds (and unmapped ids) pass through unchanged.
  const rewriteReferenceId = async (ref: ReferenceId): Promise<ReferenceId> => {
    if (ref.kind === "task" || ref.kind === "page") {
      const mapped =
        ref.kind === "task" ? taskIdMap.get(ref.id) : pageIdMap.get(ref.id);
      return mapped ? { ...ref, id: mapped } : ref;
    }
    if (ref.kind !== "object") return ref;
    let parsed: {
      workspace_id?: unknown;
      table_id?: unknown;
      row_id?: unknown;
    };
    try {
      parsed = await controller.graph.object.id.parse(ref.id);
    } catch {
      return ref;
    }
    const tableId =
      typeof parsed.table_id === "string" ? parsed.table_id : null;
    const rowId = typeof parsed.row_id === "string" ? parsed.row_id : null;
    if (!tableId || !rowId) return ref;
    const destTableId = dbIdMap[tableId] ?? tableId;
    const destRowId = mapRowId(tableId, rowId);
    if (!destRowId) return ref;
    const newId = await controller.graph.object.id.make({
      workspace_id: workspaceId,
      table_id: destTableId,
      row_id: destRowId,
    });
    return { ...ref, id: newId };
  };

  // Rewrite a packaged `AiPrompt` (`{ document, context? }`).
  const rewritePrompt = async (prompt: JsonValue): Promise<AiPrompt> => {
    const parsed = parsedPackagePrompts.get(prompt);
    if (!parsed) {
      throw new Error("AI prompt was not registered during import preflight");
    }
    const document = await rewritePromptDocument(parsed.document);
    const references = parsed.context?.references
      ? await Promise.all(
          parsed.context.references.map(async (reference) => ({
            ...reference,
            data: await rewriteReferenceId(reference.data),
          }))
        )
      : undefined;
    const context = parsed.context
      ? {
          ...parsed.context,
          ...(references === undefined ? {} : { references }),
        }
      : undefined;
    return {
      document,
      ...(context === undefined ? {} : { context }),
    };
  };

  // Repoint a `commit_pattern` trigger at the freshly imported databases.
  //
  // A `commit_pattern` trigger fires when rows change in the databases listed in
  // `watched`. Those entries still reference the *exporter's* workspace/table
  // ids, which don't exist for the importing user, so we rewrite each watched
  // pattern:
  //   - `workspace_id` -> the importing user's workspace.
  //   - `table_id` (only for `table_row` patterns, i.e. custom databases) ->
  //     the newly-created table id via `dbIdMap`; built-in kinds like
  //     task/event/page live in system databases and need no id remap.
  //
  // Everything else is preserved as-is. In particular `on_change` (which events
  // — creation/update/deletion — the automation reacts to) is NOT a database
  // pointer and is left untouched. `manual`/`recurrent` triggers watch no
  // database and pass through unchanged.
  const rewriteTrigger = (
    trigger: AiAutomationSimpleTrigger
  ): AiAutomationSimpleTrigger => {
    if (trigger.trigger !== "commit_pattern") return trigger;
    const watched = trigger.watched.map((pattern) =>
      pattern.kind === "table_row"
        ? {
            ...pattern,
            workspace_id: workspaceId,
            table_id: dbIdMap[pattern.table_id] ?? pattern.table_id,
          }
        : { ...pattern, workspace_id: workspaceId }
    );
    return { ...trigger, watched };
  };

  const rewriteRelationRecord = (value: GraphResult): GraphResult | null => {
    const packagedRowId = extractRoutineRowId(value);
    const rawTableId = extractRoutineDatabaseId(value);
    if (!packagedRowId || !rawTableId) return null;
    // Relation values are graph results, which can carry alias ids for the
    // built-in tables (see `definitiveTableId`); normalize before mapping.
    const packagedTableId = definitiveTableId(rawTableId);

    const destTableId = dbIdMap[packagedTableId] ?? packagedTableId;
    const destRowId = mapRowId(packagedTableId, packagedRowId);
    if (!destRowId) {
      logger.debug("relation dropped: missing row mapping", {
        packagedTableId,
        packagedRowId,
      });
      return null;
    }

    const db = dbById.get(String(destTableId)) ?? null;
    const databaseValue: Record<string, GraphResult> = db
      ? graphDatabaseToGraphResultDatabase(db)
      : { id: destTableId };

    // Use the minimal shape used elsewhere in the app for relation values:
    // `{ _routine: { id, database } }`.
    // Keeping extra packaged keys can break the controller command compiler.
    const out: Record<string, GraphResult> = {
      _routine: {
        id: destRowId,
        database: {
          ...databaseValue,
          id: destTableId,
          workspace: workspaceId,
        },
      },
    };
    return out;
  };

  const rewriteRelationValue = (v: JsonValue): GraphResult => {
    if (Array.isArray(v)) {
      const out: GraphResult[] = [];
      for (const item of v) {
        if (!isGraphResult(item)) continue;
        const rewritten = rewriteRelationRecord(item);
        if (rewritten) out.push(rewritten);
      }
      return out;
    }
    if (!isGraphResult(v)) return null;
    const rewritten = rewriteRelationRecord(v);
    return rewritten ?? null;
  };

  // 5) Second pass: set relation + notes fields using rewritten ids.
  progressSet(`Finalizing objects…`);
  let finalizedRowIndex = 0;
  for (const { packagedDbId, packagedRowId, data } of rowsToCreate) {
    finalizedRowIndex += 1;
    const destDbIdForMessage = dbIdMap[packagedDbId];
    const destDbForMessage = destDbIdForMessage
      ? (dbById.get(String(destDbIdForMessage)) ?? null)
      : null;
    const destLabel =
      destDbForMessage?.data_name ??
      destDbForMessage?.name ??
      (destDbIdForMessage ? String(destDbIdForMessage) : packagedDbId);
    const objectLabel = objectLabelFromRow(data, packagedRowId);
    const message = `(${finalizedRowIndex}/${rowsToCreate.length}) Finalizing object ${objectLabel} as ${destLabel}`;
    progressSet(message);
    try {
      const destDbId = dbIdMap[packagedDbId];
      if (!destDbId) continue;
      const newRowId = mapRowId(packagedDbId, packagedRowId);
      if (!newRowId) continue;

      const byName = fieldTypeByName(packagedDbId);
      const dataRec = isJsonRecord(data) ? data : null;
      if (!dataRec) continue;

      let relationFieldsSet = 0;
      let notesFieldsSet = 0;
      for (const [name, raw] of Object.entries(dataRec)) {
        // Skip internal/system fields (we reserve a leading "_" for these, e.g. `_routine`).
        if (!name || name.startsWith("_")) continue;
        const t = byName.get(name);
        if (!t) continue;

        if (t && isRelationType(t)) {
          const value = rewriteRelationValue(raw);
          await setRelationField(runCommand, destDbId, newRowId, name, value);
          relationFieldsSet += 1;
        } else if (isNoteType(t)) {
          const doc = await rewriteNotesDocument(raw);
          if (!doc) continue;
          await setNotesField(runCommand, destDbId, newRowId, name, doc);
          notesFieldsSet += 1;
          logger.debug("notes.field.set", {
            packagedDbId,
            packagedRowId,
            destDbId,
            newRowId,
          });
        }
      }
      if (relationFieldsSet > 0 || notesFieldsSet > 0) {
        logger.debug("row.update (pass2)", {
          packagedDbId,
          packagedRowId,
          destDbId,
          newRowId,
          relationFieldsSet,
          notesFieldsSet,
        });
      }
    } finally {
      progressDone += 1;
      progressSet(message);
    }
  }

  // 5b) Finalize the built-in tasks/pages: their extension rows' relation and
  // notes custom fields (mirroring pass 2, against the personal workspace),
  // then their own notes — rewritten at the ProseMirror layer like everything
  // else, and written through the entities' dedicated RPCs (their notes are
  // not reachable through graph commands).
  let finalizedEntityIndex = 0;
  for (const entity of defaultEntities) {
    finalizedEntityIndex += 1;
    const message = `(${finalizedEntityIndex}/${defaultEntities.length}) Finalizing ${entity.kind} ${entity.meta.title}`;
    progressSet(message);
    try {
      const newId =
        entity.kind === "task"
          ? taskIdMap.get(entity.meta.id)
          : pageIdMap.get(entity.meta.id);
      if (!newId) continue;

      const ext = entity.meta.extensionRow;
      const db = extensionTableByKind.get(entity.kind);
      const extRowId = ext && db ? mapRowId(String(db.id), ext.rowId) : null;
      if (
        ext &&
        db &&
        extRowId &&
        personalRunner &&
        isJsonRecord(ext.properties)
      ) {
        const byName = fieldTypeByName(String(db.id));
        for (const [name, raw] of Object.entries(ext.properties)) {
          if (!name || name.startsWith("_")) continue;
          const t = byName.get(name);
          if (!t) continue;
          if (isRelationType(t)) {
            const value = rewriteRelationValue(raw);
            await setRelationField(
              personalRunner,
              db.id,
              extRowId,
              name,
              value
            );
          } else if (isNoteType(t)) {
            const doc = await rewriteNotesDocument(raw);
            if (!doc) continue;
            await setNotesField(personalRunner, db.id, extRowId, name, doc);
          }
        }
      }

      const parsedNotes = parsedEntityNotes.get(entity);
      if (parsedNotes) {
        const pmDoc = documentToProseMirrorDoc(parsedNotes, baseLogger);
        const notes = regenerateBlockIds(
          proseMirrorDocToDocument(await rewritePmDoc(pmDoc))
        );
        if (entity.kind === "task") {
          await controller.task.update(newId, { notes }, "app", null);
        } else {
          await controller.page.update(newId, { notes }, "app");
        }
      }
      logger.debug("entity.finalize ok", {
        kind: entity.kind,
        packagedId: entity.meta.id,
        newId,
      });
    } finally {
      progressDone += 1;
      progressSet(message);
    }
  }

  // 6) Rebuild AI entities (agents / automations / steps) using ai.* RPCs.
  // Graph objects have been created above, so dbIdMap + row maps are available
  // to rewrite prompt references and automation triggers.
  const agentIdMap = new Map<string, AnyAgentId>();
  const automationIdMap = new Map<string, AutomationId>();

  const rebuildStep = async (
    orgId: OrgId,
    step: PackagedStep
  ): Promise<StepId> => {
    const prompt = await rewritePrompt(step.meta.prompt);
    return controller.ai.step.create(
      orgId,
      { name: step.meta.name ?? "", prompt },
      null
    );
  };

  const rebuildAutomation = async (
    orgId: OrgId,
    automation: PackagedAutomation,
    agentId: AnyAgentId | null
  ): Promise<AutomationId | null> => {
    const existing = automationIdMap.get(automation.meta.id);
    if (existing) return existing;

    const stepIds: StepId[] = [];
    for (const step of automation.steps) {
      stepIds.push(await rebuildStep(orgId, step));
    }

    const newAutomationId = await controller.ai.automation.create(
      orgId,
      automation.meta.name ?? "",
      stepIds,
      agentId
    );
    automationIdMap.set(automation.meta.id, newAutomationId);

    const patch: AiAutomationPatch = {};
    if (typeof automation.meta.enabled === "boolean") {
      patch.enabled = automation.meta.enabled;
    }
    if (automation.meta.trigger) {
      patch.trigger = rewriteTrigger(automation.meta.trigger);
    }
    if (Object.keys(patch).length > 0) {
      await controller.ai.automation.update(orgId, newAutomationId, patch);
    }
    return newAutomationId;
  };

  const rebuildAgent = async (
    orgId: OrgId,
    agent: PackagedAgent
  ): Promise<AnyAgentId | null> => {
    const existing = agentIdMap.get(agent.meta.id);
    if (existing) return existing;

    const newAgentId: AgentId = await controller.ai.agent.create(
      orgId,
      agent.meta.key ?? "",
      agent.meta.name ?? null,
      ""
    );
    agentIdMap.set(agent.meta.id, newAgentId);

    await controller.ai.agent.update(orgId, newAgentId, {
      agent_instructions: await rewritePrompt(agent.meta.agent_instructions),
      user_preferences: await rewritePrompt(agent.meta.user_preferences),
      working_memory: await rewritePrompt(agent.meta.working_memory),
    });

    for (const automation of agent.automations) {
      await rebuildAutomation(orgId, automation, newAgentId);
    }
    return newAgentId;
  };

  const agents = collectAgents(pkg.root);
  const automations = collectAutomations(pkg.root);
  if ((agents.length > 0 || automations.length > 0) && !organizationId) {
    logger.error(
      "importPackage(): package contains AI entities but no organizationId was provided; skipping AI rebuild"
    );
  } else if (organizationId) {
    const orgId = organizationId;
    progressSet("Rebuilding agents and automations…");
    // Agents first so their automations register before the standalone pass.
    for (const agent of agents) await rebuildAgent(orgId, agent);
    for (const automation of automations) {
      const agentId =
        pkg.root.kind === "automation" &&
        automation.meta.id === pkg.root.meta.id
          ? (rootAutomationAgentId ?? null)
          : null;
      await rebuildAutomation(orgId, automation, agentId);
    }
  }

  let rootObject: { databaseId: TableId; id: string } | undefined;
  let importedRoot: ImportedRoot | undefined;

  if (pkg.root.kind === "object") {
    const rootPackagedDbId = pkg.root.meta.tableId;
    const rootPackagedRowId = pkg.root.meta.rowId;
    const rootDatabaseId = dbIdMap[rootPackagedDbId];
    const rootRowId = mapRowId(rootPackagedDbId, rootPackagedRowId);
    rootObject =
      rootDatabaseId && rootRowId
        ? { databaseId: rootDatabaseId, id: rootRowId }
        : undefined;

    if (!rootObject) {
      logger.error("root object mapping missing", {
        rootPackagedDbId,
        rootPackagedRowId,
        hasDbMapping: Boolean(rootDatabaseId),
        hasRowMapping: Boolean(rootRowId),
      });
    } else {
      importedRoot = { kind: "object", ...rootObject };
      logger.debug("root object mapped", {
        rootPackagedDbId,
        rootPackagedRowId,
        rootDatabaseId: rootObject.databaseId,
        rootRowId: rootObject.id,
      });
    }
  } else if (pkg.root.kind === "agent") {
    const id = agentIdMap.get(pkg.root.meta.id);
    if (id) importedRoot = { kind: "agent", id };
  } else if (pkg.root.kind === "task") {
    const id = taskIdMap.get(pkg.root.meta.id);
    if (id) importedRoot = { kind: "task", id };
  } else if (pkg.root.kind === "page") {
    const id = pageIdMap.get(pkg.root.meta.id);
    if (id) importedRoot = { kind: "page", id };
  } else {
    const id = automationIdMap.get(pkg.root.meta.id);
    if (id) importedRoot = { kind: "automation", id };
  }

  logger.debug("importPackage() completed", {
    dbIdMap,
    createdRows: rowsToCreate.length,
    tasksCreated: taskIdMap.size,
    pagesCreated: pageIdMap.size,
    agentsCreated: agentIdMap.size,
    automationsCreated: automationIdMap.size,
  });
  progressDone = progressTotal;
  progressSet("Install complete.");
  return { dbIdMap, blockIdMap, rootObject, root: importedRoot };
}
